/**
 * Report Generator Service
 * Orchestrates report generation with template engine and data processing
 */

import ReportTemplateEngine from '../templates/ReportTemplateEngine';
import { REPORT_TYPE_DEFINITIONS, REPORT_STATUS, REPORT_FORMATS } from '../types/reportTypes';

class ReportGeneratorService {
  constructor() {
    this.generationQueue = new Map();
    this.generationHistory = [];
    this.maxHistorySize = 100;
  }

  /**
   * Generate a report with specified format and options
   */
  async generateReport(reportType, data, options = {}) {
    const {
      format = REPORT_FORMATS.HTML,
      includeMetadata = true,
      customOptions = {}
    } = options;

    // Validate report type
    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    if (!reportDef) {
      throw new Error(`Unknown report type: ${reportType}`);
    }

    // Validate format support
    if (!reportDef.supportedFormats.includes(format)) {
      throw new Error(`Format ${format} not supported for report type ${reportType}`);
    }

    const generationId = this.generateId();
    
    try {
      // Add to generation queue
      this.generationQueue.set(generationId, {
        id: generationId,
        type: reportType,
        format,
        status: REPORT_STATUS.GENERATING,
        startTime: new Date(),
        progress: 0
      });

      // Update progress
      this.updateProgress(generationId, 25);

      // Process data for the specific report type
      const processedData = this.preprocessData(reportType, data);
      
      this.updateProgress(generationId, 50);

      // Generate content based on format
      let content;
      const metadata = this.generateMetadata(reportType, data, options);

      switch (format) {
        case REPORT_FORMATS.HTML:
          content = await this.generateHTMLReport(reportType, processedData, customOptions);
          break;
        case REPORT_FORMATS.PDF:
          content = await this.generatePDFReport(reportType, processedData, customOptions);
          break;
        case REPORT_FORMATS.CSV:
          content = await this.generateCSVReport(reportType, processedData, customOptions);
          break;
        case REPORT_FORMATS.EXCEL:
          content = await this.generateExcelReport(reportType, processedData, customOptions);
          break;
        case REPORT_FORMATS.JSON:
          content = await this.generateJSONReport(reportType, processedData, customOptions);
          break;
        default:
          throw new Error(`Unsupported format: ${format}`);
      }

      this.updateProgress(generationId, 90);

      // Prepare final result
      const result = {
        id: generationId,
        type: reportType,
        format,
        content,
        metadata: includeMetadata ? metadata : null,
        generatedAt: new Date(),
        status: REPORT_STATUS.COMPLETED
      };

      // Add to history
      this.addToHistory(result);
      
      // Remove from queue
      this.generationQueue.delete(generationId);
      
      this.updateProgress(generationId, 100);

      return result;

    } catch (error) {
      console.error(`Error generating ${reportType} report:`, error);
      
      // Update queue status
      const queueItem = this.generationQueue.get(generationId);
      if (queueItem) {
        queueItem.status = REPORT_STATUS.FAILED;
        queueItem.error = error.message;
      }

      throw error;
    }
  }

  /**
   * Generate HTML report using template engine
   */
  async generateHTMLReport(reportType, data, options = {}) {
    const templateOptions = {
      includeStyles: options.includeStyles !== false,
      printOptimized: options.printOptimized !== false,
      title: options.title,
      subtitle: options.subtitle
    };

    return ReportTemplateEngine.generateReportHTML(reportType, data, templateOptions);
  }

  /**
   * Generate PDF report (placeholder for PDF generation)
   */
  async generatePDFReport(reportType, data, options = {}) {
    // First generate HTML
    const htmlContent = await this.generateHTMLReport(reportType, data, {
      ...options,
      printOptimized: true
    });

    // In a real implementation, you would use a library like puppeteer or jsPDF
    // For now, return a placeholder that includes the HTML
    return {
      type: 'pdf-placeholder',
      htmlContent,
      note: 'PDF generation would be implemented here using puppeteer or similar library',
      suggestedLibrary: 'puppeteer',
      implementationHint: 'Convert HTML to PDF using headless Chrome'
    };
  }

  /**
   * Generate CSV report
   */
  async generateCSVReport(reportType, data, options = {}) {
    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    
    if (!reportDef.columns || !data.data) {
      throw new Error('Cannot generate CSV: Missing column definitions or data');
    }

    const { delimiter = ',', includeHeaders = true } = options;
    const rows = [];

    // Add headers
    if (includeHeaders) {
      const headers = reportDef.columns.map(col => col.label);
      rows.push(headers.join(delimiter));
    }

    // Add data rows based on report type
    const dataRows = this.extractDataForCSV(reportType, data.data, reportDef.columns);
    dataRows.forEach(row => {
      const csvRow = row.map(cell => {
        // Escape cells that contain delimiter, quotes, or newlines
        const cellStr = String(cell || '');
        if (cellStr.includes(delimiter) || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      });
      rows.push(csvRow.join(delimiter));
    });

    return rows.join('\n');
  }

  /**
   * Generate Excel report (placeholder)
   */
  async generateExcelReport(reportType, data, options = {}) {
    // In a real implementation, you would use a library like xlsx or exceljs
    return {
      type: 'excel-placeholder',
      data: data.data,
      note: 'Excel generation would be implemented here using xlsx library',
      suggestedLibrary: 'xlsx',
      implementationHint: 'Use XLSX.writeFile() to generate Excel files'
    };
  }

  /**
   * Generate JSON report
   */
  async generateJSONReport(reportType, data, options = {}) {
    const { pretty = true } = options;
    const jsonData = {
      reportType,
      generatedAt: new Date().toISOString(),
      ...data
    };

    return pretty ? JSON.stringify(jsonData, null, 2) : JSON.stringify(jsonData);
  }

  /**
   * Extract data for CSV format based on report type
   */
  extractDataForCSV(reportType, data, columns) {
    const rows = [];

    switch (reportType) {
      case 'daily-log':
        if (data.reservations) {
          data.reservations.forEach(reservation => {
            rows.push([
              reservation.reservationNumber,
              `${reservation.customer?.firstName || ''} ${reservation.customer?.lastName || ''}`.trim(),
              reservation.golfCart?.cartNumber || reservation.inventoryItem?.name || 'N/A',
              reservation.startTime,
              reservation.endTime || 'Active',
              reservation.status,
              reservation.totalAmount || 0
            ]);
          });
        }
        break;

      case 'employee-performance':
        if (data.performanceData) {
          data.performanceData.forEach(employee => {
            rows.push([
              employee.employee?.name || 'Unknown',
              employee.totalReservations || 0,
              employee.completedReservations || 0,
              employee.totalRevenue || 0,
              employee.averageRevenue || 0,
              employee.completionRate || 0
            ]);
          });
        }
        break;

      case 'customer-activity':
        if (data.topCustomers) {
          data.topCustomers.forEach(customer => {
            rows.push([
              customer.name || 'Unknown',
              customer.phone || 'N/A',
              customer.reservationCount || 0,
              customer.totalSpent || 0
            ]);
          });
        }
        break;

      default:
        // Generic extraction for other report types
        if (Array.isArray(data)) {
          data.forEach(item => {
            const row = columns.map(col => {
              const value = this.getNestedValue(item, col.key);
              return value || '';
            });
            rows.push(row);
          });
        }
        break;
    }

    return rows;
  }

  /**
   * Preprocess data for specific report types
   */
  preprocessData(reportType, data) {
    // Add report type to data for template engine
    const processedData = {
      ...data,
      type: reportType,
      generatedAt: new Date().toISOString()
    };

    // Report-specific preprocessing
    switch (reportType) {
      case 'daily-log':
        return this.preprocessDailyLogData(processedData);
      case 'revenue':
        return this.preprocessRevenueData(processedData);
      case 'employee-performance':
        return this.preprocessEmployeePerformanceData(processedData);
      default:
        return processedData;
    }
  }

  /**
   * Preprocess daily log data
   */
  preprocessDailyLogData(data) {
    if (data.data && data.data.reservations) {
      // Sort reservations by start time
      data.data.reservations.sort((a, b) => 
        new Date(a.startTime) - new Date(b.startTime)
      );
    }
    return data;
  }

  /**
   * Preprocess revenue data
   */
  preprocessRevenueData(data) {
    // Calculate additional metrics if not provided
    if (data.data && !data.data.averageRevenue) {
      const total = data.data.totalRevenue || 0;
      const count = data.data.totalReservations || 1;
      data.data.averageRevenue = count > 0 ? total / count : 0;
    }
    return data;
  }

  /**
   * Preprocess employee performance data
   */
  preprocessEmployeePerformanceData(data) {
    if (data.data && data.data.performanceData) {
      // Calculate completion rates
      data.data.performanceData.forEach(employee => {
        if (!employee.completionRate && employee.totalReservations > 0) {
          employee.completionRate = (employee.completedReservations / employee.totalReservations) * 100;
        }
      });
    }
    return data;
  }

  /**
   * Generate metadata for report
   */
  generateMetadata(reportType, data, options) {
    const reportDef = REPORT_TYPE_DEFINITIONS[reportType];
    
    return {
      reportType,
      reportName: reportDef.name,
      reportDescription: reportDef.description,
      generatedAt: new Date().toISOString(),
      parameters: data.parameters || {},
      format: options.format || REPORT_FORMATS.HTML,
      dataSource: 'Condor Golf Cart Management System',
      version: '1.0.0'
    };
  }

  /**
   * Update generation progress
   */
  updateProgress(generationId, progress) {
    const item = this.generationQueue.get(generationId);
    if (item) {
      item.progress = progress;
      item.lastUpdate = new Date();
    }
  }

  /**
   * Get generation status
   */
  getGenerationStatus(generationId) {
    return this.generationQueue.get(generationId) || null;
  }

  /**
   * Get all active generations
   */
  getActiveGenerations() {
    return Array.from(this.generationQueue.values());
  }

  /**
   * Add report to history
   */
  addToHistory(report) {
    this.generationHistory.unshift({
      id: report.id,
      type: report.type,
      format: report.format,
      generatedAt: report.generatedAt,
      status: report.status
    });

    // Trim history if too long
    if (this.generationHistory.length > this.maxHistorySize) {
      this.generationHistory = this.generationHistory.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Get generation history
   */
  getGenerationHistory(limit = 10) {
    return this.generationHistory.slice(0, limit);
  }

  /**
   * Generate unique ID for report generation
   */
  generateId() {
    return `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get nested object value safely
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current && current[key], obj);
  }

  /**
   * Clear generation queue
   */
  clearQueue() {
    this.generationQueue.clear();
  }

  /**
   * Cancel generation
   */
  cancelGeneration(generationId) {
    const item = this.generationQueue.get(generationId);
    if (item) {
      item.status = REPORT_STATUS.CANCELLED;
      item.cancelledAt = new Date();
    }
  }
}

export default new ReportGeneratorService();